import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FlightDetails from "./FlightResults/FlightDetails";
import AirlineLogo from "./FlightResults/AirlineLogo";
import {
  FaPlane,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaChair,
  FaSuitcase,
  FaExchangeAlt,
  FaCheckCircle,
  FaSpinner,
  FaInfoCircle,
  FaUtensils,
  FaTag,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPriceSync } from "../utils/currency";
import { formatTimeAMPM } from "../utils/flightUtils";

const RoundTripFlightCard = ({
  flight,
  index,
  formatApiDateTime,
  minutesToHm,
  verifyFlightPrice,
  fetchSSROptions,
  handleAuthCheck,
  statusRefdId,
}) => {
  const { returnRefId, setOnwardRefID } = useAuth();
  const { currency } = useCurrency();

  // Debug log

  const onwardFlight = flight?.onwardFlight || flight?.onward || {};
  const returnFlight = flight?.returnFlight || flight?.return || {};

  // State management
  const [onwardFareOptions, setOnwardFareOptions] = useState([]);
  const [returnFareOptions, setReturnFareOptions] = useState([]);
  const [loadingOnwardFares, setLoadingOnwardFares] = useState(false);
  const [loadingReturnFares, setLoadingReturnFares] = useState(false);
  const [selectedOnwardFare, setSelectedOnwardFare] = useState(null);
  const [selectedReturnFare, setSelectedReturnFare] = useState(null);
  const [showOnwardFares, setShowOnwardFares] = useState(false);
  const [showReturnFares, setShowReturnFares] = useState(false);
  const [expandedOnwardDetails, setExpandedOnwardDetails] = useState(false);
  const [expandedReturnDetails, setExpandedReturnDetails] = useState(false);

  // Helper functions
  const getSegments = (leg) => {
    if (!leg) return [];

    // Try different possible data structures
    if (Array.isArray(leg)) {
      return leg.filter((s) => s && s.flightNo);
    }

    if (leg?.Flights?.Onward) {
      if (Array.isArray(leg.Flights.Onward)) {
        return leg.Flights.Onward.filter((s) => s && s.flightNo);
      }
      // If it's an object, convert to array
      return Object.values(leg.Flights.Onward).filter((s) => s && s.flightNo);
    }

    if (typeof leg === "object") {
      const values = Object.values(leg);
      return values.filter((s) => s && s.flightNo);
    }

    return [];
  };

  const getStopsText = (leg) => {
    const segs = getSegments(leg);
    const stops = Math.max(0, segs.length - 1);
    if (stops === 0) return "Non-stop";
    const vias = segs
      .slice(0, -1)
      .map((s) => s.arrCode)
      .filter(Boolean)
      .join(", ");
    return `${stops} Stop${stops > 1 ? "s" : ""}${vias ? ` via ${vias}` : ""}`;
  };

  const getTotalDurationMinutes = (leg) => {
    if (!leg) return 0;

    // Try different duration properties
    if (leg.durTotal) return Number(leg.durTotal);
    if (leg?.Fares?.[0]?.duration) return Number(leg.Fares[0].duration);

    const segs = getSegments(leg);
    return segs.reduce((sum, s) => sum + Number(s.duration || s.dur || 0), 0);
  };

  const onwardSegments = getSegments(onwardFlight);
  const returnSegments = getSegments(returnFlight);

  const onwardFirstSegment = onwardSegments[0] || {};
  const onwardLastSegment = onwardSegments[onwardSegments.length - 1] || {};
  const returnFirstSegment = returnSegments[0] || {};
  
  // V2: Extract enhanced fare information
  const onwardFare = onwardFlight?.Fare || onwardFlight?.fare || {};
  const returnFare = returnFlight?.Fare || returnFlight?.fare || {};
  
  // V2: Get fare type information
  const getFareTypeBadge = (fare) => {
    const fareTypeDesc = fare?.fareTypeDesc?.toLowerCase() || "";
    if (fareTypeDesc.includes("student")) return { text: "Student", color: "bg-green-100 text-green-800" };
    if (fareTypeDesc.includes("senior")) return { text: "Senior Citizen", color: "bg-purple-100 text-purple-800" };
    if (fareTypeDesc.includes("defence")) return { text: "Defence", color: "bg-blue-100 text-blue-800" };
    if (fareTypeDesc.includes("sme")) return { text: "SME", color: "bg-yellow-100 text-yellow-800" };
    if (fareTypeDesc.includes("promo") || fareTypeDesc.includes("offer")) return { text: "Promo", color: "bg-orange-100 text-orange-800" };
    return null;
  };
  
  const onwardFareBadge = getFareTypeBadge(onwardFare);
  const returnFareBadge = getFareTypeBadge(returnFare);
  
  // V2: Terminal and operating airline info
  const onwardDepTerminal = onwardFirstSegment?.depTer || "";
  const onwardArrTerminal = onwardLastSegment?.arrTer || "";
  const returnDepTerminal = returnFirstSegment?.depTer || "";
  const returnArrTerminal = returnSegments[returnSegments.length - 1]?.arrTer || "";
  
  const onwardOperatingAirline = onwardFirstSegment?.airNameOp;
  const returnOperatingAirline = returnFirstSegment?.airNameOp;
  const returnLastSegment = returnSegments[returnSegments.length - 1] || {};

  // Get base prices from flight data
  const baseOnwardPrice = parseInt(
    onwardFlight?.Fare?.total?.total || onwardFlight?.Fares?.[0]?.price || 9451
  );
  const baseReturnPrice = parseInt(
    returnFlight?.Fare?.total?.total || returnFlight?.Fares?.[0]?.price || 4517
  );

  // Get airline names
  const onwardAirlineName =
    onwardFlight?.Flights?.AirlineName ||
    onwardFirstSegment?.airName ||
    onwardFlight?.airline ||
    "Airline";
  const returnAirlineName =
    returnFlight?.Flights?.AirlineName ||
    returnFirstSegment?.airName ||
    returnFlight?.airline ||
    "Airline";

  const processFareOptions = (apiFares, flightData, type) => {
    if (!apiFares || !Array.isArray(apiFares)) {
      return [];
    }

    return apiFares.map((fare, index) => {
      // Extract SSR info specific to this fare
      let fareSSRInfo = null;
      let fareBaggageOptions = [];
      let fareMealOptions = [];

      if (fare.ssrInfo) {
        fareSSRInfo = fare.ssrInfo;
        // Check if SSR info is directly on the fare or in Onward property
        if (fareSSRInfo.Onward) {
          fareBaggageOptions = fareSSRInfo.Onward.Bagg || [];
          fareMealOptions = fareSSRInfo.Onward.Meal || [];
        } else if (fareSSRInfo.Bagg || fareSSRInfo.Meal) {
          // Some fares might have SSR info directly
          fareBaggageOptions = fareSSRInfo.Bagg || [];
          fareMealOptions = fareSSRInfo.Meal || [];
        }

        // Also check for baggageOption/mealOption properties (for bundled fares)
        if (fareSSRInfo.baggageOption) {
          fareBaggageOptions = [fareSSRInfo.baggageOption];
        }
        if (fareSSRInfo.mealOption) {
          fareMealOptions = [fareSSRInfo.mealOption];
        }
      }

      // Get baggage info from fare data
      const baggageInfo = fare.baggage || {};
      const flightsData = flightData?.Flights || {};
      const baggageText = baggageInfo.checkin
        ? `${baggageInfo.checkin.replace(
            "|",
            ""
          )} | ${baggageInfo.cabin.replace("|", "")} `
        : flightsData?.bagCkin?.split(" ")[0];

      return {
        id: `${type}-fare-${index}`,
        name: fare.fareName || `Fare ${index + 1}`,
        price: parseInt(
          fare.price || (type === "onward" ? baseOnwardPrice : baseReturnPrice)
        ),
        base: parseInt(fare.base || 0),
        tax: parseInt(fare.tax || 0),
        refundable: fare.refundable || "Partially Refundable*",
        baggage: baggageText,
        dateChangeFee: fare.dateChangeFee || "2,999",
        meals: fare.meals === "available" ? "Available" : "Paid",
        seats: fare.seats === "chargeable" ? "Paid" : "Free",
        color: type === "onward" ? "blue" : "green",
        type: type,
        flightData: flightData,
        ssrInfo: fareSSRInfo,
        baggageOptions: fareBaggageOptions,
        mealOptions: fareMealOptions,
        originalFare: fare,
        flightID: fare.flightID,
        refID: fare.refID,
        validation: fare.validation || fare.Validation || flightData?.Validation || {},
      };
    });
  };

  const handleViewAllFares = async () => {
    if (showOnwardFares && showReturnFares) {
      setShowOnwardFares(false);
      setShowReturnFares(false);
      return;
    }

    setLoadingOnwardFares(true);
    setLoadingReturnFares(true);

    try {
      const onwardFlightId = onwardFirstSegment?.flightID || onwardFlight?.flightId;
      const returnFlightId = returnFirstSegment?.flightID || returnFlight?.flightId;

      if (!onwardFlightId || !statusRefdId || !returnFlightId || !returnRefId) {
        throw new Error("Missing flight ID or reference ID for round trip");
      }

      setOnwardRefID(statusRefdId);

      // Fetch both onward and return fares concurrently
      const [onwardFareOptions, returnFareOptions] = await Promise.all([
        verifyFlightPrice(onwardFlightId, statusRefdId, false, onwardFlight),
        verifyFlightPrice(returnFlightId, returnRefId, false, returnFlight)
      ]);

      // Process both API responses
      const processedOnwardFares = processFareOptions(onwardFareOptions, onwardFlight, "onward");
      const processedReturnFares = processFareOptions(returnFareOptions, returnFlight, "return");

      if (processedOnwardFares.length === 0) {
        processedOnwardFares.push({
          id: "onward-default",
          name: "Standard Fare",
          price: baseOnwardPrice,
          refundable: "Partially Refundable*",
          baggage: onwardFlight?.Flights?.bagCkin?.split(" ")[0],
          dateChangeFee: "Contact Airlines",
          meals: "Paid",
          seats: "Paid",
          color: "blue",
          type: "onward",
          flightData: onwardFlight,
          ssrInfo: null,
          baggageOptions: [],
          mealOptions: [],
          validation: onwardFare?.validation || onwardFare?.Validation || {},
        });
      }

      if (processedReturnFares.length === 0) {
        processedReturnFares.push({
          id: "return-default",
          name: "Standard Fare",
          price: baseReturnPrice,
          refundable: "Partially Refundable*",
          baggage: returnFlight?.Flights?.bagCkin?.split(" ")[0],
          dateChangeFee: "Contact Airlines",
          meals: "Paid",
          seats: "Paid",
          color: "green",
          type: "return",
          flightData: returnFlight,
          ssrInfo: null,
          baggageOptions: [],
          mealOptions: [],
          validation: returnFare?.validation || returnFare?.Validation || {},
        });
      }

      setOnwardFareOptions(processedOnwardFares);
      setReturnFareOptions(processedReturnFares);
      
      setShowOnwardFares(true);
      setShowReturnFares(true);
    } catch (error) {
      console.error("Error fetching fare options:", error);
      
      const defaultOnward = [{
        id: "onward-default",
        name: "Standard Fare",
        price: baseOnwardPrice,
        refundable: "Partially Refundable*",
        baggage: onwardFlight?.Flights?.bagCkin?.split(" ")[0],
        dateChangeFee: "Contact Airlines",
        meals: "Paid",
        seats: "Paid",
        color: "blue",
        type: "onward",
        flightData: onwardFlight,
        ssrInfo: null,
        baggageOptions: [],
        mealOptions: [],
        validation: onwardFare?.validation || onwardFare?.Validation || {},
      }];

      const defaultReturn = [{
        id: "return-default",
        name: "Standard Fare",
        price: baseReturnPrice,
        refundable: "Partially Refundable*",
        baggage: returnFlight?.Flights?.bagCkin?.split(" ")[0],
        dateChangeFee: "Contact Airlines",
        meals: "Paid",
        seats: "Paid",
        color: "green",
        type: "return",
        flightData: returnFlight,
        ssrInfo: null,
        baggageOptions: [],
        mealOptions: [],
        validation: returnFare?.validation || returnFare?.Validation || {},
      }];

      setOnwardFareOptions(defaultOnward);
      setReturnFareOptions(defaultReturn);
      setShowOnwardFares(true);
      setShowReturnFares(true);
    } finally {
      setLoadingOnwardFares(false);
      setLoadingReturnFares(false);
    }
  };

  // V2: On fare select, call Price Verify to get bookingRefID + SSR for this leg
  const handleSelectOnwardFare = async (fare) => {
    const flightId = fare.flightID || onwardFirstSegment?.flightID;
    const refId = fare.refID || statusRefdId;
    /*
    if (fetchSSROptions && flightId && refId) {
      try {
        const ssrData = await fetchSSROptions(flightId, refId, false);
        const baggageOptions = ssrData.ssrInfo?.Onward?.Bagg || ssrData.ssrInfo?.Onward?.bagg || fare.baggageOptions || [];
        const mealOptions = ssrData.ssrInfo?.Onward?.Meal || ssrData.ssrInfo?.Onward?.meal || fare.mealOptions || [];
        setSelectedOnwardFare({
          ...fare,
          bookingRefID: ssrData.bookingRefID || refId,
          ssrInfo: ssrData.ssrInfo,
          baggageOptions,
          mealOptions,
          priceCheckData: ssrData.validationRequirements,
        });
      } catch (e) {
        setSelectedOnwardFare({ ...fare, bookingRefID: refId });
      }
    } else {
      setSelectedOnwardFare({ ...fare, bookingRefID: fare.refID || statusRefdId });
    }
    */
    setSelectedOnwardFare({ ...fare, bookingRefID: fare.refID || statusRefdId });
    setShowOnwardFares(false);
  };

  const handleSelectReturnFare = async (fare) => {
    const flightId = fare.flightID || returnFirstSegment?.flightID;
    const refId = fare.refID || returnRefId;
    /*
    if (fetchSSROptions && flightId && refId) {
      try {
        const ssrData = await fetchSSROptions(flightId, refId, false);
        const baggageOptions = ssrData.ssrInfo?.Onward?.Bagg || ssrData.ssrInfo?.Onward?.bagg || fare.baggageOptions || [];
        const mealOptions = ssrData.ssrInfo?.Onward?.Meal || ssrData.ssrInfo?.Onward?.meal || fare.mealOptions || [];
        setSelectedReturnFare({
          ...fare,
          bookingRefID: ssrData.bookingRefID || refId,
          ssrInfo: ssrData.ssrInfo,
          baggageOptions,
          mealOptions,
          priceCheckData: ssrData.validationRequirements,
        });
      } catch (e) {
        setSelectedReturnFare({ ...fare, bookingRefID: refId });
      }
    } else {
      setSelectedReturnFare({ ...fare, bookingRefID: fare.refID || returnRefId });
    }
    */
    setSelectedReturnFare({ ...fare, bookingRefID: fare.refID || returnRefId });
    setShowReturnFares(false);
  };

  const handleBookNow = () => {
    if (!selectedOnwardFare || !selectedReturnFare) {
      alert("Please select fares for both onward and return flights");
      return;
    }

    const totalPrice = selectedOnwardFare.price + selectedReturnFare.price;

    // V2 Doc: Use refID from Price Verify (Status of Price Request) for each leg
    const onwardRefID = selectedOnwardFare.bookingRefID || selectedOnwardFare.refID || statusRefdId;
    const returnRefIdBooking = selectedReturnFare.bookingRefID || selectedReturnFare.refID || returnRefId;

    const bookingData = {
      isRoundTrip: true,
      onwardFlight: {
        ...onwardFlight,
        selectedFare: selectedOnwardFare,
      },
      returnFlight: {
        ...returnFlight,
        selectedFare: selectedReturnFare,
      },
      totalPrice: totalPrice,
      flightID: onwardFirstSegment?.flightID,
      refID: statusRefdId,
      onwardRefID,
      returnRefId: returnRefIdBooking,
      selectedOnwardFare,
      selectedReturnFare,
    };

    handleAuthCheck(bookingData);
  };

  // Calculate total price based on selections
  const totalSelectedPrice =
    (selectedOnwardFare?.price || baseOnwardPrice) +
    (selectedReturnFare?.price || baseReturnPrice);

  // Format time in AM/PM
  const formatTime = (dateString, timeStr) => formatTimeAMPM(dateString, timeStr);

  const formatDate = (dateString) => {
    if (!dateString) return "--/--";

    if (formatApiDateTime) {
      const formatted = formatApiDateTime(dateString);
      return formatted?.split(",")[0] || "--/--";
    }

    // Fallback parsing
    if (dateString.length >= 8) {
      const day = dateString.slice(6, 8);
      const month = dateString.slice(4, 6);
      return `${day}/${month}`;
    }

    return "--/--";
  };

  // Get flight duration
  const onwardDuration = minutesToHm
    ? minutesToHm(getTotalDurationMinutes(onwardFlight))
    : `${Math.floor(getTotalDurationMinutes(onwardFlight) / 60)}h ${
        getTotalDurationMinutes(onwardFlight) % 60
      }m`;

  const returnDuration = minutesToHm
    ? minutesToHm(getTotalDurationMinutes(returnFlight))
    : `${Math.floor(getTotalDurationMinutes(returnFlight) / 60)}h ${
        getTotalDurationMinutes(returnFlight) % 60
      }m`;

  // Check if flight data exists
  if (
    !onwardFlight ||
    Object.keys(onwardFlight).length === 0 ||
    !returnFlight ||
    Object.keys(returnFlight).length === 0
  ) {
    console.warn("Missing flight data for round trip card");
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-6">
        <p className="text-slate-500 text-sm">Flight data not available</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-3 hover:shadow-md transition-all duration-200 max-w-5xl mx-auto"
    >
      {/* Compact Header - Responsive */}
      <div className="bg-white px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
          <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
            <div className="p-1.5 sm:p-2 rounded-lg bg-gray-100 border border-gray-200 flex-shrink-0">
              <FaExchangeAlt className="text-blue-600 text-xs sm:text-sm" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <h3 className="text-xs sm:text-sm font-bold text-gray-900">Round Trip</h3>
                {onwardFareBadge && (
                  <span className={`px-1.5 py-0.5 text-xs font-semibold rounded flex-shrink-0 ${onwardFareBadge.color}`}>
                    {onwardFareBadge.text}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">
                <span className="hidden sm:inline">{onwardAirlineName} → {onwardLastSegment?.arrCode || "---"} • {returnAirlineName} → {returnLastSegment?.arrCode || "---"}</span>
                <span className="sm:hidden">{onwardFirstSegment?.depCode || "---"} ↔ {returnLastSegment?.arrCode || "---"}</span>
              </p>
            </div>
          </div>
          <div className="text-left sm:text-right w-full sm:w-auto flex items-center justify-between sm:block space-x-4">
            <div className="flex flex-col">
              <div className="text-base sm:text-lg font-bold text-blue-600">
                {formatPriceSync(totalSelectedPrice, currency)}
              </div>
              <p className="text-xs text-gray-500 hidden sm:block">Total • Select fares below</p>
              <p className="text-xs text-gray-500 sm:hidden">Total</p>
            </div>
            <button
              onClick={handleViewAllFares}
              disabled={loadingOnwardFares || loadingReturnFares}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-semibold flex items-center justify-center transition-colors"
            >
              {(loadingOnwardFares || loadingReturnFares) ? <FaSpinner className="animate-spin mr-2" /> : null}
              {showOnwardFares && showReturnFares ? "Hide Fares" : "Check Fares"}
            </button>
          </div>
        </div>
      </div>

      {/* ONWARD FLIGHT – clear section separation */}
      <div className="border-b-2 border-gray-200 border-l-4 border-l-blue-500 bg-white">
        <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-blue-50 to-white border-b border-blue-100">
          <span className="text-xs sm:text-sm font-bold text-blue-800 uppercase tracking-wide">Onward Flight</span>
          <span className="text-xs text-gray-600 ml-1 sm:ml-2 hidden sm:inline">• {formatDate(onwardFirstSegment?.depDate)} • {onwardFirstSegment?.depCode || "---"} → {onwardLastSegment?.arrCode || "---"}</span>
          <span className="text-xs text-gray-600 ml-1 sm:hidden">• {onwardFirstSegment?.depCode || "---"} → {onwardLastSegment?.arrCode || "---"}</span>
        </div>
        <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-b border-gray-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
              <AirlineLogo
                airlineCode={onwardFirstSegment?.airCode || ""}
                airlineName={onwardAirlineName}
                className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <h4 className="text-xs sm:text-sm font-bold text-gray-900 truncate">{onwardAirlineName}</h4>
                <p className="text-xs text-gray-500 truncate">{onwardFirstSegment?.flightNo || "---"}</p>
              </div>
            </div>
            <div className="text-left sm:text-right w-full sm:w-auto">
              <div className="text-sm sm:text-base font-bold text-gray-900">
                {selectedOnwardFare ? formatPriceSync(selectedOnwardFare.price, currency) : "Select fare"}
              </div>
              {onwardFlight?.Flights?.bagCkin && (
                <p className="text-xs text-gray-500">{onwardFlight.Flights.bagCkin}</p>
              )}
            </div>
          </div>
          {/* Route bar - Responsive */}
          <div className="flex items-center justify-between py-2 gap-2 sm:gap-4">
            <div className="text-center flex-1 min-w-0">
              <div className="text-sm sm:text-base font-bold text-gray-900 truncate">{onwardFirstSegment?.depCode || "---"}</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-700">{formatTime(onwardFirstSegment?.depDate)}</div>
              <div className="text-xs text-gray-500 truncate">{formatDate(onwardFirstSegment?.depDate)}</div>
            </div>
            <div className="flex-1 px-2 sm:px-4 min-w-0">
              <div className="flex items-center">
                <div className="flex-1 border-t border-dashed border-gray-400" />
                <div className="mx-1 sm:mx-2 relative flex-shrink-0">
                  <FaPlane className="text-blue-500 text-xs" />
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 whitespace-nowrap hidden sm:block">
                    {onwardDuration}
                  </div>
                </div>
                <div className="flex-1 border-t border-dashed border-gray-400" />
              </div>
              <div className="text-center text-xs text-gray-500 mt-1 truncate">
                <span className="sm:hidden">{onwardDuration} • </span>{getStopsText(onwardFlight)}
              </div>
            </div>
            <div className="text-center flex-1 min-w-0">
              <div className="text-sm sm:text-base font-bold text-gray-900 truncate">{onwardLastSegment?.arrCode || "---"}</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-700">{formatTime(onwardLastSegment?.arrDate)}</div>
              <div className="text-xs text-gray-500 truncate">{formatDate(onwardLastSegment?.arrDate)}</div>
            </div>
          </div>
          {/* Selected fare summary – clear base + baggage + add-ons */}
          {selectedOnwardFare && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
              <div className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">Selected – Onward</div>
              <div className="flex justify-between items-start text-sm">
                <div>
                  <span className="font-semibold text-gray-900">{selectedOnwardFare.name || selectedOnwardFare.fareTypeDesc || "Fare"}</span>
                  <span className="text-gray-500 mx-1">•</span>
                  <span className="text-gray-700">{selectedOnwardFare.baggage || "—"}</span>
                  <div className="text-xs text-gray-500 mt-0.5">Base: {formatPriceSync(selectedOnwardFare.price || 0, currency)} per person</div>
                </div>
                <FaCheckCircle className="text-green-500 flex-shrink-0" />
              </div>
              {selectedOnwardFare.ssrInfo && (selectedOnwardFare.ssrInfo.baggageOption || selectedOnwardFare.ssrInfo.mealOption) && (
                <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-gray-700">
                  <span className="font-medium text-gray-800">Add-ons: </span>
                  {selectedOnwardFare.ssrInfo.baggageOption && <span>{selectedOnwardFare.ssrInfo.baggageOption.baggDesc} (+{formatPriceSync(selectedOnwardFare.ssrInfo.baggageOption.baggAmt, currency)})</span>}
                  {selectedOnwardFare.ssrInfo.baggageOption && selectedOnwardFare.ssrInfo.mealOption && " • "}
                  {selectedOnwardFare.ssrInfo.mealOption && <span>{selectedOnwardFare.ssrInfo.mealOption.mealDesc} (+{formatPriceSync(selectedOnwardFare.ssrInfo.mealOption.mealAmt, currency)})</span>}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-3 sm:px-4 py-2 bg-white border-b border-gray-200 flex items-center justify-between gap-2">
          <button
            onClick={() => setExpandedOnwardDetails(!expandedOnwardDetails)}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center min-h-[36px] sm:min-h-0"
          >
            <span className="hidden sm:inline">Flight Details</span>
            <span className="sm:hidden">Details</span>
            {expandedOnwardDetails ? <FaChevronUp className="ml-1" size={10} /> : <FaChevronDown className="ml-1" size={10} />}
          </button>
        </div>

            {/* Onward Fare Options – one-way style table */}
            <AnimatePresence>
              {showOnwardFares && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-gray-200 bg-white"
                >
                  <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-blue-50 to-slate-50 text-xs font-bold text-gray-800 border-b-2 border-gray-200">
                    <div className="col-span-3 flex items-center">
                      <FaTag className="mr-2 text-blue-600" />
                      Base Fare Type
                    </div>
                    <div className="col-span-2 flex items-center">
                      <FaSuitcase className="mr-2 text-blue-600" />
                      Included Baggage
                    </div>
                    <div className="col-span-2 flex items-center">
                      <FaUtensils className="mr-2 text-blue-600" />
                      Included Services
                    </div>
                    <div className="col-span-5 flex items-center justify-end">
                      <span className="text-blue-600 font-semibold">₹</span> Base Price / Select
                    </div>
                  </div>
                  {loadingOnwardFares ? (
                    <div className="text-center py-6">
                      <FaSpinner className="animate-spin text-blue-500 text-xl mx-auto" />
                      <p className="text-gray-500 mt-2 text-sm">Loading fare options...</p>
                    </div>
                  ) : onwardFareOptions.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {onwardFareOptions.map((fare, idx) => {
                        const isSelected = selectedOnwardFare?.id === fare.id;
                        return (
                          <div
                            key={fare.id || idx}
                            className={`md:grid md:grid-cols-12 md:gap-2 px-3 sm:px-4 py-3 sm:py-4 hover:bg-blue-50/50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0 ${isSelected ? "bg-blue-50 border-l-4 border-blue-500 md:border-l-4" : ""}`}
                            onClick={() => handleSelectOnwardFare(fare)}
                          >
                            {/* Mobile Layout - Stacked */}
                            <div className="md:hidden space-y-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900 mb-1">{fare.name || fare.fareTypeDesc || `Fare ${idx + 1}`}</div>
                                  <div className="text-lg font-bold text-gray-900 mb-1">{formatPriceSync(fare.price || 0, currency)}</div>
                                  <div className="text-xs text-gray-500">Total Base Fare</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleSelectOnwardFare(fare); }}
                                  className={`px-3 py-2 rounded text-xs font-semibold flex-shrink-0 ${isSelected ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                                >
                                  {isSelected ? "Selected" : "Select"}
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <div className="font-semibold text-gray-700 mb-1">Baggage</div>
                                  <div className="text-gray-600">{fare.baggage || fare.baggageCheckin || "—"}</div>
                                  {fare.baggageCabin && <div className="text-gray-500 text-xs mt-0.5">Cabin: {fare.baggageCabin}</div>}
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-700 mb-1">Services</div>
                                  <div className="space-y-0.5 text-gray-600">
                                    <div>Seats: {fare.seatsAvailable || fare.seats || "—"}</div>
                                    <div className={fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "text-green-600" : "text-gray-600"}>
                                      Meal: {fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}
                                    </div>
                                    {fare.validation?.gstInd !== undefined && (
                                      <div className={fare.validation.gstInd === "1" ? "text-amber-700 font-medium" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"}>
                                        GST: {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                                      </div>
                                    )}
                                    {fare.validation?.allowFQT !== undefined && (
                                      <div className="text-gray-600">FQT: {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className={`text-xs ${fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "text-red-600 font-medium" : "text-green-600 font-medium"}`}>
                                {fare.refundable || "Partially Refundable"}
                              </div>
                            </div>

                            {/* Desktop Layout - Grid */}
                            <div className="hidden md:block col-span-3">
                              <div className="text-sm font-semibold text-gray-900">{fare.name || fare.fareTypeDesc || `Fare ${idx + 1}`}</div>
                              <div className={`text-xs mt-0.5 ${fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "text-red-600" : "text-green-600"}`}>
                                {fare.refundable || "Partially Refundable"}
                              </div>
                            </div>
                            <div className="hidden md:block col-span-2 text-xs text-gray-700">
                              <div className="font-medium">{fare.baggage || fare.baggageCheckin || "—"}</div>
                              {fare.baggageCabin && <div className="text-gray-500">Cabin: {fare.baggageCabin}</div>}
                            </div>
                            <div className="hidden md:block col-span-2 text-xs text-gray-700 space-y-0.5">
                              <div>Seats: {fare.seatsAvailable || fare.seats || "—"}</div>
                              <div className={fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "text-green-600" : "text-gray-600"}>
                                Meal – {fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}
                              </div>
                              {fare.validation?.gstInd !== undefined && (
                                <div className={fare.validation.gstInd === "1" ? "text-amber-700" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"}>
                                  GST – {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                                </div>
                              )}
                              {fare.validation?.allowFQT !== undefined && (
                                <div className={fare.validation.allowFQT === "1" ? "text-green-600" : "text-gray-600"}>
                                  FQT – {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}
                                </div>
                              )}
                            </div>
                            <div className="hidden md:flex col-span-5 items-center justify-end gap-3">
                              <div className="text-right">
                                <div className="text-lg font-bold text-gray-900">{formatPriceSync(fare.price || 0, currency)}</div>
                                <div className="text-xs text-gray-500">Total Base Fare</div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleSelectOnwardFare(fare); }}
                                className={`px-3 py-1.5 rounded text-xs font-semibold ${isSelected ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                              >
                                {isSelected ? "Selected" : "Select Base Fare"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500 text-sm">No fare options available</div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Onward Flight Details - expandable like one-way */}
            <AnimatePresence>
              {expandedOnwardDetails && onwardSegments.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-4 mt-4 border-t border-gray-200 pt-4 pb-4 bg-gray-50"
                >
                  <FlightDetails flight={onwardFlight} segments={onwardSegments} formatApiDateTime={formatApiDateTime} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

      {/* Divider: Onward vs Return – clear visual separation */}
      <div className="h-1 bg-gradient-to-r from-blue-200 via-gray-200 to-green-200" aria-hidden="true" />

      {/* RETURN FLIGHT – clear section separation */}
      <div className="border-b-2 border-gray-200 border-l-4 border-l-green-500 bg-white">
        <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-white border-b border-green-100">
          <span className="text-sm font-bold text-green-800 uppercase tracking-wide">Return Flight</span>
          <span className="text-xs text-gray-600 ml-2">• {formatDate(returnFirstSegment?.depDate)} • {returnFirstSegment?.depCode || "---"} → {returnLastSegment?.arrCode || "---"}</span>
        </div>
            <div className="px-4 py-3 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <AirlineLogo airlineCode={returnFirstSegment?.airCode || ""} airlineName={returnAirlineName} className="w-10 h-10" />
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">{returnAirlineName}</h4>
                    <p className="text-xs text-gray-500">{returnFirstSegment?.flightNo || "---"}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold text-gray-900">
                    {selectedReturnFare ? formatPriceSync(selectedReturnFare.price, currency) : "Select fare"}
                  </div>
                  {returnFlight?.Flights?.bagCbin && <p className="text-xs text-gray-500">{returnFlight.Flights.bagCbin}</p>}
                </div>
              </div>
              <div className="flex items-center justify-between py-2 gap-2 sm:gap-4">
                <div className="text-center flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-bold text-gray-900 truncate">{returnFirstSegment?.depCode || "---"}</div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-700">{formatTime(returnFirstSegment?.depDate)}</div>
                  <div className="text-xs text-gray-500 truncate">{formatDate(returnFirstSegment?.depDate)}</div>
                </div>
                <div className="flex-1 px-2 sm:px-4 min-w-0">
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-dashed border-gray-400" />
                    <div className="mx-1 sm:mx-2 relative flex-shrink-0">
                      <FaPlane className="text-green-500 text-xs" />
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 whitespace-nowrap hidden sm:block">{returnDuration}</div>
                    </div>
                    <div className="flex-1 border-t border-dashed border-gray-400" />
                  </div>
                  <div className="text-center text-xs text-gray-500 mt-1 truncate">
                    <span className="sm:hidden">{returnDuration} • </span>{getStopsText(returnFlight)}
                  </div>
                </div>
                <div className="text-center flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-bold text-gray-900 truncate">{returnLastSegment?.arrCode || "---"}</div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-700">{formatTime(returnLastSegment?.arrDate)}</div>
                  <div className="text-xs text-gray-500 truncate">{formatDate(returnLastSegment?.arrDate)}</div>
                </div>
              </div>
              {selectedReturnFare && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border-2 border-green-200">
                  <div className="text-xs font-semibold text-green-800 uppercase tracking-wide mb-2">Selected – Return</div>
                  <div className="flex justify-between items-start text-sm">
                    <div>
                      <span className="font-semibold text-gray-900">{selectedReturnFare.name || selectedReturnFare.fareTypeDesc || "Fare"}</span>
                      <span className="text-gray-500 mx-1">•</span>
                      <span className="text-gray-700">{selectedReturnFare.baggage || "—"}</span>
                      <div className="text-xs text-gray-500 mt-0.5">Base: {formatPriceSync(selectedReturnFare.price || 0, currency)} per person</div>
                    </div>
                    <FaCheckCircle className="text-green-500 flex-shrink-0" />
                  </div>
                  {selectedReturnFare.ssrInfo && (selectedReturnFare.ssrInfo.baggageOption || selectedReturnFare.ssrInfo.mealOption) && (
                    <div className="mt-2 pt-2 border-t border-green-200 text-xs text-gray-700">
                      <span className="font-medium text-gray-800">Add-ons: </span>
                      {selectedReturnFare.ssrInfo.baggageOption && <span>{selectedReturnFare.ssrInfo.baggageOption.baggDesc} (+{formatPriceSync(selectedReturnFare.ssrInfo.baggageOption.baggAmt, currency)})</span>}
                      {selectedReturnFare.ssrInfo.baggageOption && selectedReturnFare.ssrInfo.mealOption && " • "}
                      {selectedReturnFare.ssrInfo.mealOption && <span>{selectedReturnFare.ssrInfo.mealOption.mealDesc} (+{formatPriceSync(selectedReturnFare.ssrInfo.mealOption.mealAmt, currency)})</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-3 sm:px-4 py-2 bg-white border-b border-gray-200 flex items-center justify-between gap-2">
              <button onClick={() => setExpandedReturnDetails(!expandedReturnDetails)} className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center min-h-[36px] sm:min-h-0">
                <span className="hidden sm:inline">Flight Details</span>
                <span className="sm:hidden">Details</span>
                {expandedReturnDetails ? <FaChevronUp className="ml-1" size={10} /> : <FaChevronDown className="ml-1" size={10} />}
              </button>
            </div>

            {/* Return Fare Options – one-way style table */}
            <AnimatePresence>
              {showReturnFares && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-gray-200 bg-white"
                >
                  <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-green-50 to-slate-50 text-xs font-bold text-gray-800 border-b-2 border-gray-200">
                    <div className="col-span-3 flex items-center">
                      <FaTag className="mr-2 text-green-600" />
                      Base Fare Type
                    </div>
                    <div className="col-span-2 flex items-center">
                      <FaSuitcase className="mr-2 text-green-600" />
                      Included Baggage
                    </div>
                    <div className="col-span-2 flex items-center">
                      <FaUtensils className="mr-2 text-green-600" />
                      Included Services
                    </div>
                    <div className="col-span-5 flex items-center justify-end">
                      <span className="text-green-600 font-semibold">₹</span> Base Price / Select
                    </div>
                  </div>
                  {loadingReturnFares ? (
                    <div className="text-center py-6">
                      <FaSpinner className="animate-spin text-green-500 text-xl mx-auto" />
                      <p className="text-gray-500 mt-2 text-sm">Loading fare options...</p>
                    </div>
                  ) : returnFareOptions.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {returnFareOptions.map((fare, idx) => {
                        const isSelected = selectedReturnFare?.id === fare.id;
                        return (
                          <div
                            key={fare.id || idx}
                            className={`md:grid md:grid-cols-12 md:gap-2 px-3 sm:px-4 py-3 sm:py-4 hover:bg-green-50/50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0 ${isSelected ? "bg-green-50 border-l-4 border-green-500 md:border-l-4" : ""}`}
                            onClick={() => handleSelectReturnFare(fare)}
                          >
                            {/* Mobile Layout - Stacked */}
                            <div className="md:hidden space-y-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900 mb-1">{fare.name || fare.fareTypeDesc || `Fare ${idx + 1}`}</div>
                                  <div className="text-lg font-bold text-gray-900 mb-1">{formatPriceSync(fare.price || 0, currency)}</div>
                                  <div className="text-xs text-gray-500">Total Base Fare</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleSelectReturnFare(fare); }}
                                  className={`px-3 py-2 rounded text-xs font-semibold flex-shrink-0 ${isSelected ? "bg-green-600 text-white" : "bg-green-600 text-white hover:bg-green-700"}`}
                                >
                                  {isSelected ? "Selected" : "Select"}
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <div className="font-semibold text-gray-700 mb-1">Baggage</div>
                                  <div className="text-gray-600">{fare.baggage || fare.baggageCheckin || "—"}</div>
                                  {fare.baggageCabin && <div className="text-gray-500 text-xs mt-0.5">Cabin: {fare.baggageCabin}</div>}
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-700 mb-1">Services</div>
                                  <div className="space-y-0.5 text-gray-600">
                                    <div>Seats: {fare.seatsAvailable || fare.seats || "—"}</div>
                                    <div className={fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "text-green-600" : "text-gray-600"}>
                                      Meal: {fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}
                                    </div>
                                    {fare.validation?.gstInd !== undefined && (
                                      <div className={fare.validation.gstInd === "1" ? "text-amber-700 font-medium" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"}>
                                        GST: {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                                      </div>
                                    )}
                                    {fare.validation?.allowFQT !== undefined && (
                                      <div className="text-gray-600">FQT: {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className={`text-xs ${fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "text-red-600 font-medium" : "text-green-600 font-medium"}`}>
                                {fare.refundable || "Partially Refundable"}
                              </div>
                            </div>

                            {/* Desktop Layout - Grid */}
                            <div className="hidden md:block col-span-3">
                              <div className="text-sm font-semibold text-gray-900">{fare.name || fare.fareTypeDesc || `Fare ${idx + 1}`}</div>
                              <div className={`text-xs mt-0.5 ${fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "text-red-600" : "text-green-600"}`}>
                                {fare.refundable || "Partially Refundable"}
                              </div>
                            </div>
                            <div className="hidden md:block col-span-2 text-xs text-gray-700">
                              <div className="font-medium">{fare.baggage || fare.baggageCheckin || "—"}</div>
                              {fare.baggageCabin && <div className="text-gray-500">Cabin: {fare.baggageCabin}</div>}
                            </div>
                            <div className="hidden md:block col-span-2 text-xs text-gray-700 space-y-0.5">
                              <div>Seats: {fare.seatsAvailable || fare.seats || "—"}</div>
                              <div className={fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "text-green-600" : "text-gray-600"}>
                                Meal – {fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" || fare.validation?.freeMeal === "1" ? "Free" : "Paid"}
                              </div>
                              {fare.validation?.gstInd !== undefined && (
                                <div className={fare.validation.gstInd === "1" ? "text-amber-700" : fare.validation.gstInd === "2" ? "text-blue-600" : "text-gray-600"}>
                                  GST – {fare.validation.gstInd === "1" ? "Mandatory" : fare.validation.gstInd === "2" ? "Optional" : "Not Allowed"}
                                </div>
                              )}
                              {fare.validation?.allowFQT !== undefined && (
                                <div className={fare.validation.allowFQT === "1" ? "text-green-600" : "text-gray-600"}>
                                  FQT – {fare.validation.allowFQT === "1" ? "Allowed" : "Not Allowed"}
                                </div>
                              )}
                            </div>
                            <div className="hidden md:flex col-span-5 items-center justify-end gap-3">
                              <div className="text-right">
                                <div className="text-lg font-bold text-gray-900">{formatPriceSync(fare.price || 0, currency)}</div>
                                <div className="text-xs text-gray-500">Total Base Fare</div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleSelectReturnFare(fare); }}
                                className={`px-3 py-1.5 rounded text-xs font-semibold ${isSelected ? "bg-green-600 text-white" : "bg-green-600 text-white hover:bg-green-700"}`}
                              >
                                {isSelected ? "Selected" : "Select Base Fare"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500 text-sm">No fare options available</div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Return Flight Details - expandable like one-way */}
            <AnimatePresence>
              {expandedReturnDetails && returnSegments.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-4 mt-4 border-t border-gray-200 pt-4 pb-4 bg-gray-50"
                >
                  <FlightDetails flight={returnFlight} segments={returnSegments} formatApiDateTime={formatApiDateTime} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        {/* Combined Booking Section - same tone as one-way, clear Onward vs Return */}
        {(selectedOnwardFare || selectedReturnFare) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-4 mt-4 border-t-2 border-gray-200 pt-4 pb-4 bg-slate-50/50"
          >
            <div className="rounded-xl border-2 border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Round Trip Summary</h3>
              <p className="text-sm text-gray-500 mb-4">Onward and return selections – review before proceeding</p>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {/* Onward – clear block */}
                <div className="rounded-lg border-2 border-blue-200 bg-blue-50/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded uppercase">Onward</span>
                    <span className="text-sm font-semibold text-gray-800">{onwardAirlineName}</span>
                  </div>
                  <div className="text-xs text-gray-600 mb-1">
                    {onwardFirstSegment?.depCode || "—"} → {onwardLastSegment?.arrCode || "—"} • {onwardFirstSegment?.flightNo || "—"}
                  </div>
                  <div className="flex justify-between items-baseline mt-2">
                    <span className="text-sm text-gray-700">{selectedOnwardFare?.name || "Fare"} • {selectedOnwardFare?.baggage || "—"}</span>
                    <span className="text-lg font-bold text-blue-700">{formatPriceSync(selectedOnwardFare?.price ?? baseOnwardPrice, currency)}</span>
                  </div>
                  {selectedOnwardFare?.ssrInfo && (selectedOnwardFare.ssrInfo.baggageOption || selectedOnwardFare.ssrInfo.mealOption) && (
                    <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-gray-700">
                      {selectedOnwardFare.ssrInfo.baggageOption && <span>Baggage: {selectedOnwardFare.ssrInfo.baggageOption.baggDesc} (+{formatPriceSync(selectedOnwardFare.ssrInfo.baggageOption.baggAmt, currency)})</span>}
                      {selectedOnwardFare.ssrInfo.baggageOption && selectedOnwardFare.ssrInfo.mealOption && " • "}
                      {selectedOnwardFare.ssrInfo.mealOption && <span>Meal: {selectedOnwardFare.ssrInfo.mealOption.mealDesc} (+{formatPriceSync(selectedOnwardFare.ssrInfo.mealOption.mealAmt, currency)})</span>}
                    </div>
                  )}
                </div>

                {/* Return – clear block */}
                <div className="rounded-lg border-2 border-green-200 bg-green-50/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded uppercase">Return</span>
                    <span className="text-sm font-semibold text-gray-800">{returnAirlineName}</span>
                  </div>
                  <div className="text-xs text-gray-600 mb-1">
                    {returnFirstSegment?.depCode || "—"} → {returnLastSegment?.arrCode || "—"} • {returnFirstSegment?.flightNo || "—"}
                  </div>
                  <div className="flex justify-between items-baseline mt-2">
                    <span className="text-sm text-gray-700">{selectedReturnFare?.name || "Fare"} • {selectedReturnFare?.baggage || "—"}</span>
                    <span className="text-lg font-bold text-green-700">{formatPriceSync(selectedReturnFare?.price ?? baseReturnPrice, currency)}</span>
                  </div>
                  {selectedReturnFare?.ssrInfo && (selectedReturnFare.ssrInfo.baggageOption || selectedReturnFare.ssrInfo.mealOption) && (
                    <div className="mt-2 pt-2 border-t border-green-200 text-xs text-gray-700">
                      {selectedReturnFare.ssrInfo.baggageOption && <span>Baggage: {selectedReturnFare.ssrInfo.baggageOption.baggDesc} (+{formatPriceSync(selectedReturnFare.ssrInfo.baggageOption.baggAmt, currency)})</span>}
                      {selectedReturnFare.ssrInfo.baggageOption && selectedReturnFare.ssrInfo.mealOption && " • "}
                      {selectedReturnFare.ssrInfo.mealOption && <span>Meal: {selectedReturnFare.ssrInfo.mealOption.mealDesc} (+{formatPriceSync(selectedReturnFare.ssrInfo.mealOption.mealAmt, currency)})</span>}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center pt-3 border-t-2 border-gray-200 mb-4">
                <span className="text-sm font-semibold text-gray-700">Total (base + add-ons)</span>
                <span className="text-2xl font-bold text-gray-900">{formatPriceSync(totalSelectedPrice, currency)}</span>
              </div>

              {/* Add-ons (Optional) – same UI as OneWayFlightCard */}
              {(selectedOnwardFare?.baggageOptions?.length > 0 ||
                selectedOnwardFare?.mealOptions?.length > 0 ||
                selectedReturnFare?.baggageOptions?.length > 0 ||
                selectedReturnFare?.mealOptions?.length > 0) && (
                <div className="bg-white rounded-lg border border-gray-300 p-4 shadow-sm mb-6">
                  <div className="mb-3">
                    <h4 className="text-sm font-bold text-gray-900 mb-1">
                      Add-ons (Optional – Pay only if selected)
                    </h4>
                    <p className="text-xs text-gray-600">
                      Enhance your journey with optional baggage and meals for each leg.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Onward Add-ons */}
                    {(selectedOnwardFare?.baggageOptions?.length > 0 || selectedOnwardFare?.mealOptions?.length > 0) && (
                      <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                        <h5 className="text-sm font-semibold text-gray-800 flex items-center">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded mr-2">Onward</span>
                          Extra services
                        </h5>
                        {selectedOnwardFare.baggageOptions?.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              <FaSuitcase className="inline mr-1" /> Baggage
                            </label>
                            <select
                              className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              value={selectedOnwardFare.ssrInfo?.baggageOption?.baggID || ""}
                              onChange={(e) => {
                                const bag = e.target.value
                                  ? selectedOnwardFare.baggageOptions.find((b) => b.baggID === e.target.value)
                                  : null;
                                setSelectedOnwardFare((prev) => ({
                                  ...prev,
                                  ssrInfo: { ...prev.ssrInfo, baggageOption: bag || undefined, mealOption: prev.ssrInfo?.mealOption },
                                }));
                              }}
                            >
                              <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                              {selectedOnwardFare.baggageOptions.map((bag, idx) => (
                                <option key={idx} value={bag.baggID}>
                                  {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {selectedOnwardFare.mealOptions?.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              <FaUtensils className="inline mr-1" /> Meal
                            </label>
                            <select
                              className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              value={selectedOnwardFare.ssrInfo?.mealOption?.mealID || ""}
                              onChange={(e) => {
                                const meal = e.target.value
                                  ? selectedOnwardFare.mealOptions.find((m) => m.mealID === e.target.value)
                                  : null;
                                setSelectedOnwardFare((prev) => ({
                                  ...prev,
                                  ssrInfo: { ...prev.ssrInfo, mealOption: meal || undefined, baggageOption: prev.ssrInfo?.baggageOption },
                                }));
                              }}
                            >
                              <option value="">No meal ({formatPriceSync(0, currency)})</option>
                              {selectedOnwardFare.mealOptions.map((meal, idx) => (
                                <option key={idx} value={meal.mealID}>
                                  {meal.mealDesc} {meal.mealRef ? `(Seg ${meal.mealRef})` : ""} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Return Add-ons */}
                    {(selectedReturnFare?.baggageOptions?.length > 0 || selectedReturnFare?.mealOptions?.length > 0) && (
                      <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                        <h5 className="text-sm font-semibold text-gray-800 flex items-center">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded mr-2">Return</span>
                          Extra services
                        </h5>
                        {selectedReturnFare.baggageOptions?.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              <FaSuitcase className="inline mr-1" /> Baggage
                            </label>
                            <select
                              className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              value={selectedReturnFare.ssrInfo?.baggageOption?.baggID || ""}
                              onChange={(e) => {
                                const bag = e.target.value
                                  ? selectedReturnFare.baggageOptions.find((b) => b.baggID === e.target.value)
                                  : null;
                                setSelectedReturnFare((prev) => ({
                                  ...prev,
                                  ssrInfo: { ...prev.ssrInfo, baggageOption: bag || undefined, mealOption: prev.ssrInfo?.mealOption },
                                }));
                              }}
                            >
                              <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                              {selectedReturnFare.baggageOptions.map((bag, idx) => (
                                <option key={idx} value={bag.baggID}>
                                  {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {selectedReturnFare.mealOptions?.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              <FaUtensils className="inline mr-1" /> Meal
                            </label>
                            <select
                              className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              value={selectedReturnFare.ssrInfo?.mealOption?.mealID || ""}
                              onChange={(e) => {
                                const meal = e.target.value
                                  ? selectedReturnFare.mealOptions.find((m) => m.mealID === e.target.value)
                                  : null;
                                setSelectedReturnFare((prev) => ({
                                  ...prev,
                                  ssrInfo: { ...prev.ssrInfo, mealOption: meal || undefined, baggageOption: prev.ssrInfo?.baggageOption },
                                }));
                              }}
                            >
                              <option value="">No meal ({formatPriceSync(0, currency)})</option>
                              {selectedReturnFare.mealOptions.map((meal, idx) => (
                                <option key={idx} value={meal.mealID}>
                                  {meal.mealDesc} {meal.mealRef ? `(Seg ${meal.mealRef})` : ""} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Book Now - same as one-way */}
              <button
                onClick={handleBookNow}
                disabled={!selectedOnwardFare || !selectedReturnFare}
                className={`w-full mt-4 px-6 py-3 rounded-lg text-sm font-bold transition-all ${
                  selectedOnwardFare && selectedReturnFare
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Proceed to Booking
              </button>
            </div>
          </motion.div>
        )}
    </motion.div>
  );
};

export default RoundTripFlightCard;
